<script lang="ts">
  // 内容口径见页尾「资料说明」：村一级的数字（人口、耕地、户数）暂未取得，
  // 页面只写能落到来源上的事实，村镇两级的统计不混写。
  type EraEvent = {
    year: string;
    title: string;
    body: string;
  };

  type Era = {
    id: string;
    label: string;
    range: string;
    events: EraEvent[];
  };

  const ancientEra: Era = {
    id: 'ancient',
    label: '古代',
    range: '汉 — 清',
    events: [
      {
        year: '汉',
        title: '成村',
        body: '地名资料记载，汉代这里已经成村。村子位于豫东平原，临近古汴水与包河。',
      },
      {
        year: '唐宋',
        title: '汴水渡口',
        body: '唐、宋时期，村子毗邻汴水，设有渡口。这是村名故事发生之前，最早的渡口记忆。',
      },
      {
        year: '金 · 天会年间',
        title: '邓斌治河',
        body: '归德府睢阳县设管河主簿，邓斌任其事。包河决口时，他扛起土包跳入水中堵口，被浪卷走。后人把渡口称作「邓斌口」，村子也沿用了这个名字。',
      },
      {
        year: '明 · 洪武年间',
        title: '杨氏迁入',
        body: '杨氏先人随明初大移民迁来，见这里水系发达、土地肥沃，便定居下来，开荒垦地。至今约六百年，族人已繁衍近三十代。',
      },
    ],
  };

  const modernEra: Era = {
    id: 'modern',
    label: '近现代',
    range: '1928 — 1965',
    events: [
      { year: '1928', title: '王菜园联保', body: '属第十区王菜园联保。' },
      { year: '1946', title: '朱集镇第十五保', body: '属朱集镇第十五保。' },
      { year: '1949', title: '刘口区邢庄乡', body: '属刘口区邢庄乡。' },
      { year: '1950', title: '邓斌口乡', body: '属谢集区邓斌口乡——这是村名第一次成为乡一级的建制名称。' },
      {
        year: '1956',
        title: '高级生产合作社',
        body: '建秦小庄高级生产合作社，属潘口乡。',
      },
      {
        year: '1957',
        title: '包河最后一次决口',
        body: '同年夏天，包河在邓斌口决口。这是有记载的包河最后一次决口，此后未再出现决口记录。',
      },
      { year: '1961', title: '八一人民公社', body: '属商丘市郊区八一人民公社。' },
      {
        year: '1962',
        title: '设邓斌口人民公社',
        body: '废大队设邓斌口人民公社，属谢集区。',
      },
      { year: '1965', title: '复置大队', body: '复置大队，属李庄人民公社。' },
    ],
  };

  const contemporaryEra: Era = {
    id: 'contemporary',
    label: '当代',
    range: '1983 — 今',
    events: [
      {
        year: '1983.12',
        title: '村民委员会',
        body: '改设邓斌口村民委员会，属李庄乡，沿用至今。',
      },
      {
        year: '2022.06.21',
        title: '撤乡设镇',
        body: '梁园区撤销李庄乡，设立李庄镇，邓斌口村属李庄镇。',
      },
      {
        year: '2024 — 2025',
        title: '道路从村边经过',
        body: '据梁园区政府征地公告，庄周大道、梁园路以及 S209 至黄河路连接线建设，村周边部分耕地被征收为交通运输用地。',
      },
      {
        year: '2025.10.05',
        title: '村室门口的饺子宴',
        body: '村「两委」在村室门口组织孝善饺子宴，镇卫生院义诊、志愿者理发，乡村大舞台上连唱戏曲；七十岁以上老人领到毛巾，八十岁以上领到棉被。',
      },
    ],
  };

  const eras: Era[] = [ancientEra, modernEra, contemporaryEra];
  let activeEra = $state(ancientEra.id);
  const currentEra = $derived(eras.find((era) => era.id === activeEra) ?? ancientEra);

  const sections = [
    { id: 'village', no: '01', label: '村情' },
    { id: 'history', no: '02', label: '沿革' },
    { id: 'legend', no: '03', label: '传说' },
    { id: 'clans', no: '04', label: '姓氏' },
    { id: 'life', no: '05', label: '风物' },
    { id: 'today', no: '06', label: '今日' },
  ];

  const seasons = [
    {
      name: '春',
      title: '麦苗返青',
      body: '正月过后，平原上的冬小麦开始返青。风大，地平线很远，田埂上能看见整片麦田由黄转绿。',
    },
    {
      name: '夏',
      title: '收麦与播种',
      body: '芒种前后收麦，紧接着种玉米、大豆。麦收是一年里最紧的几天，晒场和村道上都是新麦的气味。',
    },
    {
      name: '秋',
      title: '玉米成熟',
      body: '秋分前后，玉米、大豆陆续成熟。河堤上的树叶转黄，田里开始腾茬、翻地。',
    },
    {
      name: '冬',
      title: '麦田休眠',
      body: '入冬后麦田沉睡，村庄进入一年里最安静的时节。走亲戚、办年货，日子围着春节慢下来。',
    },
  ];

  const sources = [
    '博雅地名网《梁园区李庄镇邓斌口村》：地名由来、地理位置、历史沿革。',
    '《李田楼杨氏分支家谱中记载的老家邓斌口》，锦秋文谱，2021-04-23：邓斌堵口传说、杨氏迁入、潭坑与红心鸭蛋。',
    '《中国河湖大典·淮河篇》包河条目：邓斌口潭坑约二十公顷、1957 年包河在邓斌口决口。',
    '河南省行政区划与地名学会《李庄乡》概况：26 个行政村、116 个自然村、耕地 6.5 万亩、沙土土质、包河境内长度 10 公里；为镇级数据。',
    '百度百科「李庄镇」词条：位于梁园区西北部，南接商丘市区，北邻山东曹县，北部有约 30 公里黄河故道大堤。',
    '梁园区人民政府 2024 年第五批、2025 年第十批及第二十一批城市建设用地征地公告。',
    '河南县域经济网，2025-10-06：《商丘市梁园区李庄镇：开展双节孝善活动 以爱为馅包出乡村振兴“暖心味”》。',
  ];
</script>

<div class="site">
  <header class="site-header">
    <a class="wordmark" href="#top">
      <span class="wordmark-cn">邓斌口</span>
      <span class="wordmark-en">DENG BINKOU</span>
    </a>
    <nav aria-label="主导航">
      {#each sections as section}
        <a href={`#${section.id}`}>{section.label}</a>
      {/each}
    </nav>
  </header>

  <main id="top">
    <section class="hero" aria-labelledby="hero-title">
      <div class="hero-copy">
        <p class="eyebrow">河南省商丘市梁园区李庄镇</p>
        <h1 id="hero-title">豫东平原上，<br />一个治水人的名字。</h1>
        <p class="lede">
          这里没有山。地平线很远，麦田一层接一层地铺到天边。包河从村西北流过，
          村名来自金代一位跳进决口堵水的管河主簿——邓斌。
        </p>
        <div class="hero-actions">
          <a class="cta" href="#village">走进村子</a>
          <a class="quiet-link" href="#legend">先听地名故事</a>
        </div>
      </div>

      <aside class="gazetteer" aria-label="地名信息卡">
        <p class="gazetteer-title">地名小卡</p>
        <dl>
          <div>
            <dt>地名</dt>
            <dd>邓斌口村</dd>
          </div>
          <div>
            <dt>隶属</dt>
            <dd>河南省商丘市梁园区李庄镇</dd>
          </div>
          <div>
            <dt>位置</dt>
            <dd>李庄镇南部，距镇区约 4 公里</dd>
          </div>
          <div>
            <dt>水系</dt>
            <dd>包河，自村西北流过</dd>
          </div>
          <div>
            <dt>区号</dt>
            <dd>0370（商丘）</dd>
          </div>
        </dl>
      </aside>
    </section>

    <nav class="chapter-nav" aria-label="章节">
      {#each sections as section}
        <a href={`#${section.id}`}>
          <span class="chapter-no">{section.no}</span>
          <span>{section.label}</span>
        </a>
      {/each}
    </nav>

    <section id="village" class="chapter" aria-labelledby="village-title">
      <header class="chapter-head">
        <p class="kicker">01 · 村情</p>
        <h2 id="village-title">平原、河流与一个行政村</h2>
        <p class="chapter-lede">
          邓斌口村是李庄镇下辖的行政村之一。村子坐落在豫东平原，南面是商丘城区，
          镇域北面是黄河故道，包河从村西北绕过。
        </p>
      </header>

      <div class="cards">
        <article class="card">
          <p class="card-kicker">区位</p>
          <h3>商丘北郊，镇区以南</h3>
          <p>
            李庄镇位于梁园区西北部，南接商丘市区，北隔黄河故道与山东曹县相望，
            被称为商丘的「北大门」。邓斌口村在镇区以南约 4 公里处。
          </p>
        </article>
        <article class="card">
          <p class="card-kicker">建置</p>
          <h3>从联保、乡到行政村</h3>
          <p>
            1928 年起，村子的上级建制多次变化：联保、保、乡、高级社、人民公社、大队。
            1983 年 12 月改设村民委员会；2022 年 6 月李庄撤乡设镇，属李庄镇至今。
          </p>
        </article>
        <article class="card">
          <p class="card-kicker">水土</p>
          <h3>沙质土与两条水脉</h3>
          <p>
            李庄镇一带属暖温带半湿润大陆性季风气候，土壤以沙土为主，耕地约 6.5 万亩。
            镇域内包河流经约 10 公里；镇北是约 30 公里的黄河故道大堤。
          </p>
        </article>
      </div>

      <figure class="schematic" aria-labelledby="schematic-caption">
        <div class="schematic-grid">
          <span class="schematic-label north">黄河故道 · 北</span>
          <span class="schematic-label west">包河 · 西北</span>
          <span class="schematic-village">邓斌口村</span>
          <span class="schematic-label town">李庄镇 · 北 · 约 4 公里</span>
          <span class="schematic-label city">商丘城区 · 南</span>
        </div>
        <figcaption id="schematic-caption">
          区位示意，不按比例。包河自西北绕村而过，李庄镇在村北，商丘城区在村南。
        </figcaption>
      </figure>
    </section>

    <section id="history" class="chapter" aria-labelledby="history-title">
      <header class="chapter-head">
        <p class="kicker">02 · 沿革</p>
        <h2 id="history-title">从汉代的聚落，到今天的行政村</h2>
        <p class="chapter-lede">
          村子的名字记在地名资料里，建制的名字换过很多次：联保、保、乡、公社、大队、村委会。
          下面按三个时段排列。
        </p>
      </header>

      <div class="era-tabs" role="group" aria-label="选择时段">
        {#each eras as era}
          <button
            type="button"
            class:active={era.id === activeEra}
            aria-pressed={era.id === activeEra}
            onclick={() => (activeEra = era.id)}
          >
            <span class="era-label">{era.label}</span>
            <span class="era-range">{era.range}</span>
          </button>
        {/each}
      </div>

      <ol class="timeline" aria-live="polite">
        {#each currentEra.events as event}
          <li>
            <p class="timeline-year">{event.year}</p>
            <div class="timeline-body">
              <h3>{event.title}</h3>
              <p>{event.body}</p>
            </div>
          </li>
        {/each}
      </ol>
    </section>

    <section id="legend" class="chapter" aria-labelledby="legend-title">
      <header class="chapter-head">
        <p class="kicker">03 · 传说</p>
        <h2 id="legend-title">一个村庄，记住了一个治水的人</h2>
      </header>

      <div class="legend-layout">
        <article class="story">
          <p class="story-lead">
            金太宗天会年间，归德府睢阳县北、黄河南岸有条包河。包河是浍河最大的支流，
            因为常在附近决口，官府在这里设官管理河道。管河主簿邓斌尽职尽责，为官清正。
          </p>
          <p>
            一年夏天，暴雨倾盆，河水暴涨。邓斌冒雨在堤上巡视，忽然一声巨响，包河南岸决口。
            当时的包河被称为「悬河」，河床高出南岸民居数丈，洪水居高临下，比猛兽还凶。
          </p>
          <p>
            邓斌没有退。他扛起土包，奋不顾身跳进水里。岸上的民众跟着一个个跳下去。
            一个恶浪打来，把他卷走了。人们在邓斌的精神鼓舞下，终于堵住了决口。
          </p>
          <p>
            后来，这里设了渡口，建了码头。为纪念这位为民捐躯的管河主簿，人们把渡口称作
            「邓斌口」。因有码头，生产兴隆，人们纷纷来此定居，村子也沿用了这个名字。
          </p>
        </article>

        <aside class="side-notes">
          <article class="note">
            <h3>潭坑与红心鸭蛋</h3>
            <p>
              村西北紧挨着包河。村西至今留有当年洪水冲击而成的潭坑，相传深不可测，
              有不少民间传说。当地还说，养在潭坑里的鸭子，下的蛋黄是赤红色的。
            </p>
          </article>
          <article class="note">
            <h3>有记载的最后一次决口</h3>
            <p>
              《中国河湖大典·淮河篇》记载，包河下行至郑阁干渠相汇处，堤南有约二十公顷的
              积水潭，即邓斌口潭坑。1957 年夏，包河在邓斌口决口，这是有记载的最后一次。
            </p>
          </article>
        </aside>
      </div>
    </section>

    <section id="clans" class="chapter" aria-labelledby="clans-title">
      <header class="chapter-head">
        <p class="kicker">04 · 姓氏</p>
        <h2 id="clans-title">明初移民，六百年繁衍</h2>
        <p class="chapter-lede">
          村子的姓氏记忆，主要留在族谱里。李田楼杨氏一支，把邓斌口记作「老家」。
        </p>
      </header>

      <div class="clan-layout">
        <div class="clan-copy">
          <p>
            据族人相传，杨氏是在明洪武年间大移民时迁来邓斌口的。
            先人看到这里水系发达、土地肥沃，便定居下来，建设家园、开荒垦地。
          </p>
          <p>
            从那时到今天，大约六百年，子孙繁衍近三十代。清代乾隆年间，十五世祖杨兆祯
            带领家人从邓斌口迁往古单县以东二十五里的李田楼，成为李田楼杨氏的一支。
          </p>
          <p class="muted">
            这只是村庄姓氏来源的一条线索。其他姓氏何时迁入、村里如今有多少户、多少口人，
            还需要村里长辈和户籍资料补充。
          </p>
        </div>
        <ul class="stats" aria-label="姓氏迁徙要点">
          <li><strong>明 · 洪武</strong><span>杨氏迁入</span></li>
          <li><strong>约 600 年</strong><span>定居至今</span></li>
          <li><strong>近 30 代</strong><span>族人繁衍</span></li>
          <li><strong>1760 年前后</strong><span>一支迁往李田楼</span></li>
        </ul>
      </div>
    </section>

    <section id="life" class="chapter" aria-labelledby="life-title">
      <header class="chapter-head">
        <p class="kicker">05 · 风物</p>
        <h2 id="life-title">麦田、包河与四季</h2>
        <p class="chapter-lede">
          平原上的日子按庄稼走。麦子、玉米、大豆是一年的主线，包河和故道是背景。
        </p>
      </header>

      <div class="season-grid">
        {#each seasons as season}
          <article class="season">
            <p class="season-name">{season.name}</p>
            <h3>{season.title}</h3>
            <p>{season.body}</p>
          </article>
        {/each}
      </div>

      <div class="life-notes">
        <article>
          <h3>田里种什么</h3>
          <p>
            李庄镇农业以粮、棉、油为主，镇域内以小麦、玉米、大豆等大田作物为主，
            也有家庭农场和种植合作社。村一级的种植结构还没有公开数据。
          </p>
        </article>
        <article>
          <h3>戏台与村室</h3>
          <p>
            2025 年双节的孝善饺子宴，包饺子、义诊、理发发生在村室门口，
            戏曲演出就在村里的乡村大舞台上。
          </p>
        </article>
        <article>
          <h3>水与树</h3>
          <p>
            镇北的黄河故道有大堤、林场和水库，是这一带少见的成片林地与水面。村西北的
            包河，则是村庄日常最亲近的一条水脉。
          </p>
        </article>
      </div>
    </section>

    <section id="today" class="chapter" aria-labelledby="today-title">
      <header class="chapter-head">
        <p class="kicker">06 · 今日</p>
        <h2 id="today-title">村庄正在发生的事</h2>
        <p class="chapter-lede">
          公开报道里最近的一条村务活动，是 2025 年双节的孝善饺子宴。
        </p>
      </header>

      <div class="today-layout">
        <article class="news-card">
          <p class="news-date">2025 年 10 月 5 日</p>
          <h3>村室门口的孝善饺子宴</h3>
          <p>
            双节期间，邓斌口村和邻村邓庄村同时办孝善活动。村「两委」组织志愿者和面、调馅、
            擀皮、包饺子；镇卫生院医护人员为老人测血压、讲慢性病护理；美发师志愿者为老人理发；
            乡村大舞台上经典戏曲轮番上演。
          </p>
          <p>
            村里给每位老人准备了月饼，七十岁以上老人加送毛巾，八十岁以上老人领到棉被。
          </p>
        </article>

        <div class="today-notes">
          <article>
            <h3>路从村边经过</h3>
            <p>
              庄周大道、梁园路和 S209 至黄河路连接线从村子周边经过。2024 年至 2025 年，
              梁园区政府多次发布征地公告，村周边部分耕地转为交通运输用地。
            </p>
          </article>
          <article>
            <h3>从乡到镇</h3>
            <p>
              2022 年 6 月 21 日，梁园区撤销李庄乡、设立李庄镇。邓斌口村随之由「李庄乡」
              改为「李庄镇」，村名不变。
            </p>
          </article>
        </div>
      </div>
    </section>

    <section id="sources" class="chapter sources" aria-labelledby="sources-title">
      <header class="chapter-head">
        <p class="kicker">资料说明</p>
        <h2 id="sources-title">这一页从哪里来</h2>
        <p class="chapter-lede">
          页面只写能落到来源上的内容。村镇两级的统计口径不同，引用时请以来源年份为准；
          标为镇级的数据不代表本村。
        </p>
      </header>
      <ol class="source-list">
        {#each sources as source}
          <li>{source}</li>
        {/each}
      </ol>
      <p class="muted small">
        村一级的人口、户数、耕地面积、主要姓氏构成等数字尚未取得。欢迎村里长辈、
        村委会和熟悉村史的人补充与指正。
      </p>
    </section>
  </main>

  <footer>
    <span>邓斌口村</span>
    <span>河南省商丘市梁园区李庄镇</span>
    <span>www.dengbinkou.com</span>
  </footer>
</div>

<style>
  .site {
    /* 村站自己的色板：麦田绿、土黄、纸色。sport 站点不受影响。 */
    --accent: #2f6b4f;
    --accent-soft: #e7f0e8;
    --paper: #fbfaf6;
    --surface: #fffdf8;
    --line: #ddd8c9;
    --text: #202a24;
    --muted: #6b7267;
    min-height: 100vh;
    background:
      radial-gradient(circle at 12% -4%, rgba(120, 160, 120, 0.16), transparent 34rem),
      linear-gradient(180deg, #f7f5ed 0%, #f4f2ea 42%, #f7f5ee 100%);
    color: var(--text);
  }

  :global(body) {
    background: #f4f2ea;
  }

  header.site-header {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    max-width: 1080px;
    margin: 0 auto;
    padding: 18px 32px;
    backdrop-filter: blur(18px);
    background: color-mix(in srgb, #f4f2ea 80%, transparent);
    border-bottom: 1px solid color-mix(in srgb, var(--line) 60%, transparent);
  }

  .wordmark {
    display: inline-flex;
    align-items: baseline;
    gap: 10px;
    white-space: nowrap;
  }

  .wordmark-cn {
    font-family: 'Songti SC', 'Noto Serif SC', 'Source Han Serif SC', serif;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.1em;
  }

  .wordmark-en {
    color: var(--muted);
    font-size: 10px;
    letter-spacing: 0.22em;
  }

  nav[aria-label='主导航'] {
    display: flex;
    gap: 24px;
    color: var(--muted);
    font-size: 14px;
  }

  nav[aria-label='主导航'] a:hover {
    color: var(--accent);
  }

  main {
    max-width: 1080px;
    margin: 0 auto;
    padding: 0 32px 72px;
  }

  .hero {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(260px, 0.9fr);
    gap: 48px;
    align-items: end;
    padding: 88px 0 64px;
  }

  .eyebrow,
  .kicker,
  .card-kicker {
    margin: 0 0 14px;
    color: var(--accent);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.18em;
  }

  h1 {
    margin: 0;
    font-family: 'Songti SC', 'Noto Serif SC', 'Source Han Serif SC', serif;
    font-size: clamp(34px, 4.6vw, 56px);
    line-height: 1.18;
    letter-spacing: -0.02em;
    font-weight: 600;
    text-wrap: balance;
  }

  .lede {
    max-width: 580px;
    margin: 26px 0 30px;
    color: var(--muted);
    font-size: 17px;
    line-height: 1.9;
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 22px;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    padding: 13px 22px;
    border-radius: 999px;
    background: var(--accent);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
  }

  .cta:hover {
    filter: brightness(1.06);
  }

  .quiet-link {
    color: var(--accent);
    font-size: 14px;
    border-bottom: 1px solid color-mix(in srgb, var(--accent) 40%, transparent);
    padding-bottom: 2px;
  }

  .gazetteer {
    padding: 22px;
    border: 1px solid var(--line);
    border-radius: 18px;
    background: var(--surface);
    box-shadow: 0 18px 40px -32px rgba(32, 42, 36, 0.45);
  }

  .gazetteer-title {
    margin: 0 0 14px;
    color: var(--accent);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
  }

  .gazetteer dl {
    display: grid;
    gap: 12px;
    margin: 0;
  }

  .gazetteer dl div {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 10px;
    border-top: 1px dashed var(--line);
    padding-top: 12px;
  }

  .gazetteer dl div:first-child {
    border-top: none;
    padding-top: 0;
  }

  .gazetteer dt {
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0.08em;
  }

  .gazetteer dd {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }

  .chapter-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 18px 0 0;
    border-top: 1px solid var(--line);
  }

  .chapter-nav a {
    display: inline-flex;
    align-items: baseline;
    gap: 7px;
    padding: 7px 13px;
    border: 1px solid var(--line);
    border-radius: 999px;
    color: var(--muted);
    font-size: 13px;
    transition: color 0.15s ease, border-color 0.15s ease;
  }

  .chapter-nav a:hover {
    color: var(--accent);
    border-color: color-mix(in srgb, var(--accent) 45%, var(--line));
  }

  .chapter-no {
    color: var(--accent);
    font-size: 10px;
    letter-spacing: 0.08em;
  }

  .chapter {
    padding: 76px 0 0;
    scroll-margin-top: 84px;
  }

  .chapter-head {
    max-width: 760px;
  }

  h2 {
    margin: 0;
    font-family: 'Songti SC', 'Noto Serif SC', 'Source Han Serif SC', serif;
    font-size: clamp(28px, 4vw, 40px);
    line-height: 1.28;
    font-weight: 600;
  }

  .chapter-lede {
    margin: 18px 0 0;
    color: var(--muted);
    font-size: 16px;
    line-height: 1.85;
  }

  h3 {
    margin: 0 0 10px;
    font-size: 18px;
    line-height: 1.45;
  }

  p {
    line-height: 1.85;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 32px;
  }

  .card {
    padding: 24px;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: var(--surface);
  }

  .card p:last-child,
  .season p:last-child,
  .note p:last-child,
  .life-notes p:last-child,
  .today-notes p:last-child,
  .news-card p:last-child {
    margin-bottom: 0;
  }

  .card p,
  .season p,
  .note p,
  .life-notes p,
  .today-notes p,
  .news-card p {
    color: var(--muted);
    font-size: 14.5px;
  }

  .schematic {
    margin: 26px 0 0;
    padding: 20px;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: color-mix(in srgb, var(--surface) 70%, transparent);
  }

  .schematic-grid {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    grid-template-rows: repeat(4, auto);
    row-gap: 26px;
    align-items: center;
    justify-items: center;
    padding: 6px 0;
  }

  .schematic-grid::before {
    content: '';
    position: absolute;
    top: 10px;
    bottom: 10px;
    left: 50%;
    border-left: 1px dashed color-mix(in srgb, var(--accent) 45%, var(--line));
  }

  .schematic-label {
    position: relative;
    padding: 2px 8px;
    background: var(--paper);
    border-radius: 6px;
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0.06em;
    white-space: nowrap;
  }

  .schematic-label.north {
    grid-column: 2;
    grid-row: 1;
  }

  .schematic-label.west {
    grid-column: 1;
    grid-row: 3;
    justify-self: end;
  }

  .schematic-label.town {
    grid-column: 2;
    grid-row: 2;
  }

  .schematic-label.city {
    grid-column: 2;
    grid-row: 4;
  }

  .schematic-village {
    position: relative;
    grid-column: 2;
    grid-row: 3;
    padding: 8px 16px;
    border-radius: 10px;
    background: var(--accent);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.08em;
    white-space: nowrap;
    box-shadow: 0 0 0 5px var(--paper);
  }

  .schematic figcaption {
    margin-top: 8px;
    color: var(--muted);
    font-size: 12px;
    line-height: 1.7;
  }

  .era-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 28px 0 8px;
  }

  .era-tabs button {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    padding: 9px 16px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: transparent;
    color: var(--muted);
    cursor: pointer;
  }

  .era-tabs button:hover {
    border-color: color-mix(in srgb, var(--accent) 45%, var(--line));
  }

  .era-tabs button.active {
    border-color: var(--accent);
    background: var(--accent);
    color: #fff;
  }

  .era-label {
    font-size: 14px;
    font-weight: 600;
  }

  .era-range {
    font-size: 11px;
    opacity: 0.8;
  }

  .timeline {
    list-style: none;
    margin: 18px 0 0;
    padding: 0;
    border-left: 2px solid color-mix(in srgb, var(--accent) 28%, var(--line));
  }

  .timeline li {
    position: relative;
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 22px;
    padding: 0 0 26px 26px;
    margin-left: -2px;
  }

  .timeline li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 9px;
    height: 9px;
    margin-left: -5.5px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 4px var(--accent-soft);
  }

  .timeline-year {
    margin: 0;
    color: var(--accent);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.7;
  }

  .timeline-body h3 {
    font-size: 16px;
  }

  .timeline-body p {
    margin: 0;
    color: var(--muted);
    font-size: 14.5px;
  }

  .legend-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(240px, 0.9fr);
    gap: 32px;
    margin-top: 32px;
  }

  .story p {
    margin: 0 0 16px;
    font-size: 16px;
  }

  .story-lead {
    color: var(--text);
  }

  .side-notes {
    display: grid;
    gap: 14px;
    align-content: start;
  }

  .note {
    padding: 20px;
    border-left: 3px solid var(--accent);
    border-radius: 0 14px 14px 0;
    background: var(--surface);
  }

  .note h3 {
    font-size: 15px;
  }

  .clan-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(240px, 0.9fr);
    gap: 40px;
    margin-top: 28px;
  }

  .clan-copy p {
    margin: 0 0 15px;
    font-size: 16px;
  }

  .stats {
    list-style: none;
    display: grid;
    gap: 12px;
    margin: 0;
    padding: 0;
  }

  .stats li {
    display: grid;
    gap: 2px;
    padding: 14px 16px;
    border: 1px solid var(--line);
    border-radius: 12px;
    background: var(--surface);
  }

  .stats strong {
    color: var(--accent);
    font-size: 19px;
    font-family: 'Songti SC', 'Noto Serif SC', 'Source Han Serif SC', serif;
    font-weight: 600;
  }

  .stats span {
    color: var(--muted);
    font-size: 12px;
    letter-spacing: 0.06em;
  }

  .season-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    margin-top: 32px;
  }

  .season {
    padding: 20px;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: var(--surface);
  }

  .season-name {
    margin: 0 0 10px;
    color: var(--accent);
    font-family: 'Songti SC', 'Noto Serif SC', 'Source Han Serif SC', serif;
    font-size: 30px;
    line-height: 1;
    font-weight: 600;
  }

  .season h3 {
    font-size: 15px;
  }

  .life-notes {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 18px;
  }

  .life-notes article,
  .today-notes article {
    padding: 22px;
    border-top: 2px solid var(--accent);
    background: color-mix(in srgb, var(--surface) 65%, transparent);
  }

  .today-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(260px, 0.9fr);
    gap: 22px;
    margin-top: 28px;
  }

  .news-card {
    padding: 26px;
    border: 1px solid var(--line);
    border-radius: 18px;
    background: var(--surface);
  }

  .news-date {
    margin: 0 0 8px;
    color: var(--accent);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
  }

  .today-notes {
    display: grid;
    gap: 14px;
    align-content: start;
  }

  .sources {
    border-top: 1px solid var(--line);
    margin-top: 64px;
  }

  .source-list {
    margin: 24px 0 16px;
    padding-left: 20px;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.9;
  }

  .source-list li {
    margin-bottom: 8px;
  }

  .muted {
    color: var(--muted);
  }

  .small {
    font-size: 13px;
  }

  footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 10px;
    max-width: 1080px;
    margin: 0 auto;
    padding: 24px 32px 40px;
    border-top: 1px solid var(--line);
    color: var(--muted);
    font-size: 13px;
  }

  @media (max-width: 900px) {
    .hero {
      grid-template-columns: 1fr;
      gap: 28px;
      padding: 56px 0 44px;
    }

    .cards,
    .life-notes {
      grid-template-columns: 1fr;
    }

    .season-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .legend-layout,
    .clan-layout,
    .today-layout {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }

  @media (max-width: 720px) {
    header.site-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 20px;
    }

    nav[aria-label='主导航'] {
      width: 100%;
      gap: 18px;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    main {
      padding: 0 20px 56px;
    }

    .chapter {
      padding-top: 56px;
    }

    .timeline li {
      grid-template-columns: 1fr;
      gap: 4px;
      padding-bottom: 22px;
    }

    .season-grid {
      grid-template-columns: 1fr;
    }

    footer {
      flex-direction: column;
      padding: 24px 20px 32px;
    }
  }

  @media (prefers-color-scheme: dark) {
    .site {
      --accent: #8fbf9f;
      --accent-soft: #1a2b20;
      --paper: #141712;
      --surface: #1b1f19;
      --line: #30362c;
      --text: #eef1ea;
      --muted: #a3aa9c;
      background:
        radial-gradient(circle at 12% -4%, rgba(70, 120, 85, 0.22), transparent 34rem),
        linear-gradient(180deg, #11140f 0%, #141712 45%, #11140f 100%);
    }

    :global(body) {
      background: #11140f;
    }

    header.site-header {
      background: color-mix(in srgb, #11140f 82%, transparent);
    }

    .cta,
    .schematic-village,
    .era-tabs button.active {
      color: #10150f;
    }

    .gazetteer {
      box-shadow: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .chapter-nav a,
    .cta {
      transition: none;
    }
  }
</style>
